import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

function AgeGate({ children }) {
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const gateRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    const verified = localStorage.getItem("ageVerified") === "true";
    if (verified) setIsVerified(true);
    setLoading(false);
  }, []);

  // Manage scroll lock and focus when gate is shown
  useEffect(() => {
    if (!loading && !isVerified) {
      previouslyFocused.current = document.activeElement;
      document.body.style.overflow = "hidden";
      // Focus the first button
      const firstBtn = gateRef.current?.querySelector("button");
      firstBtn?.focus();

      const onKeyDown = (e) => {
        if (e.key === "Escape") {
          // Do not close on ESC. Force explicit choice.
          e.preventDefault();
        }
        if (e.key === "Tab") {
          // Simple focus trap
          const focusables = gateRef.current?.querySelectorAll(
            'button, [href], [tabindex]:not([tabindex="-1"])'
          );
          if (!focusables?.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.body.style.overflow = "";
        previouslyFocused.current && previouslyFocused.current.focus?.();
      };
    }
  }, [loading, isVerified]);

  const handleVerify = () => {
    localStorage.setItem("ageVerified", "true");
    setIsVerified(true);
  };

  const handleExit = () => {
    window.location.href = "https://www.google.com";
  };

  const gate = (
    <div
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Age verification"
    >
      <div style={styles.modal} ref={gateRef} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.imageContainer}>
          <img src="/AgeGate.png" alt="" style={styles.image} />
        </div>

        <div style={styles.textContainer}>
          <h2 style={styles.title}>Are you over 18?</h2>
          <p style={styles.subtitle}>
            Hold Up Player!
            <br />
            You must be 18+ to unlock this content and
            <br />
            witness the power of the Golden Shovel
          </p>
        </div>

        <div style={styles.buttonRow}>
          <button onClick={handleVerify} style={styles.buttonPrimary}>
            I am over 18
          </button>
          <button onClick={handleExit} style={styles.buttonSecondary}>
            Exit
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {children}
      {!loading && !isVerified
        ? ReactDOM.createPortal(gate, document.getElementById("modal-root") || document.body)
        : null}
    </>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(2px)"
  },
  modal: {
    width: "90%",
    maxWidth: 400,
    backgroundColor: "#2c2c2c",
    padding: 24,
    borderRadius: 6,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    color: "#fff",
    outline: "none"
  },
  imageContainer: {
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 20
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  textContainer: {
    width: "100%",
    marginBottom: 20
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 18,
    marginTop: -4,
    color: "#efac00"
  },
  subtitle: {
    fontSize: 14,
    color: "#ccc",
    marginBottom: 8
  },
  buttonRow: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    width: "100%"
  },
  buttonPrimary: {
    padding: "10px 16px",
    backgroundColor: "#efac00",
    fontWeight: "bold",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    flex: 1
  },
  buttonSecondary: {
    padding: "10px 16px",
    backgroundColor: "#b13555",
    fontWeight: "bold",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    flex: 1
  }
};

export default AgeGate;
